import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';
import { EnforcementDialog } from './EnforcementDialog';
import { useAuth } from '../../context/AuthContext';
import { COUNTRIES, Field, inputCls, money, LegalFooter } from './shared';
import { MonthField } from '../MonthField';

export const PensionDebtForm = ({ country }) => {
  const { api } = useAuth();
  const [f, setF] = useState({ monthly_amount: '', start_month: '', end_month: new Date().toISOString().slice(0, 7), paid_total: '', annual_interest: '', annual_inflation: '' });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState(null);
  const [enfOpen, setEnfOpen] = useState(false);
  const cur = COUNTRIES.find((c) => c.code === country)?.currency;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const opt = (v) => (v === '' ? null : Number(v));
      const body = {
        country, monthly_amount: Number(f.monthly_amount), start_month: f.start_month, end_month: f.end_month,
        paid_total: Number(f.paid_total || 0), annual_interest: opt(f.annual_interest), annual_inflation: opt(f.annual_inflation),
      };
      const { data } = await api.post('/family/pension-debt', body);
      setInput(body);
      setRes(data);
    } catch (err) {
      setRes(null);
      toast.error(err.response?.data?.detail || 'No se pudo calcular');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4" data-testid="pension-debt-form">
        <Field label="Pensión mensual fijada">
          <input data-testid="pd-amount" required type="number" min="1" value={f.monthly_amount} onChange={set('monthly_amount')} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Primer mes impago"><MonthField testid="pd-start" value={f.start_month} onChange={(v) => set('start_month')({ target: { value: v } })} /></Field>
          <Field label="Último mes impago"><MonthField testid="pd-end" value={f.end_month} onChange={(v) => set('end_month')({ target: { value: v } })} /></Field>
        </div>
        <Field label="Abonos parciales recibidos (total)">
          <input data-testid="pd-paid" type="number" min="0" value={f.paid_total} onChange={set('paid_total')} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Interés anual % (opcional)" hint="Vacío = tasa legal del país">
            <input data-testid="pd-interest" type="number" step="any" min="0" value={f.annual_interest} onChange={set('annual_interest')} className={inputCls} />
          </Field>
          {country !== 'chile' && (
            <Field label="Inflación anual % (opcional)" hint="Para reajustar las cuotas">
              <input data-testid="pd-inflation" type="number" step="any" min="0" value={f.annual_inflation} onChange={set('annual_inflation')} className={inputCls} />
            </Field>
          )}
        </div>
        <button data-testid="pd-submit" disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-rose-400 transition-colors disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Calcular deuda
        </button>
      </form>
      {res && (
        <div data-testid="pd-result" className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5">
          <p className="text-xs uppercase tracking-wider text-rose-300">Deuda total actualizada · {res.months_count} meses</p>
          <p data-testid="pd-total" className="mt-1 text-4xl font-bold text-white font-['Outfit']">{money(res.total_due, cur)}</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-slate-500">Capital adeudado</dt><dd className="text-white">{money(res.total_nominal, cur)}</dd></div>
            <div><dt className="text-slate-500">Reajuste</dt><dd className="text-white">{money(res.total_readjustment, cur)}</dd></div>
            <div><dt className="text-slate-500">Intereses ({res.annual_interest_pct}% anual)</dt><dd className="text-white">{money(res.total_interest, cur)}</dd></div>
            <div><dt className="text-slate-500">Abonos</dt><dd className="text-white">− {money(res.paid_total, cur)}</dd></div>
            <div className="col-span-2"><dt className="text-slate-500">Reajuste aplicado</dt><dd className="text-slate-200">{res.readjustment_method}</dd></div>
          </dl>
          <div className="mt-4 max-h-56 overflow-y-auto rounded-lg border border-slate-800">
            <table className="w-full text-xs" data-testid="pd-table">
              <thead className="sticky top-0 bg-slate-900 text-slate-400"><tr><th className="p-2 text-left">Mes</th><th className="p-2 text-right">Cuota</th><th className="p-2 text-right">Reajuste</th><th className="p-2 text-right">Interés</th><th className="p-2 text-right">Total</th></tr></thead>
              <tbody>
                {res.rows.map((r) => (
                  <tr key={r.month} className="border-t border-slate-800 text-slate-200">
                    <td className="p-2">{r.month}</td><td className="p-2 text-right">{money(r.nominal, cur)}</td><td className="p-2 text-right">{money(r.readjustment, cur)}</td>
                    <td className="p-2 text-right">{money(r.interest, cur)}</td><td className="p-2 text-right font-semibold">{money(r.total, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LegalFooter basis={res.legal_basis} disclaimer={res.disclaimer} />
          <button data-testid="pd-enforcement-btn" onClick={() => setEnfOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-400/60 px-5 py-2 text-sm font-semibold text-rose-100 hover:bg-rose-500/10 transition-colors">
            <FileText className="h-4 w-4" /> Generar escrito de cumplimiento
          </button>
        </div>
      )}
      {res && enfOpen && <EnforcementDialog debtInput={input} open={enfOpen} onClose={() => setEnfOpen(false)} />}
    </div>
  );
};
